import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_plugin_08283616 from 'nuxt_plugin_plugin_08283616' // Source: ./components/plugin.js (mode: 'all')
import nuxt_plugin_plugin_42bab598 from 'nuxt_plugin_plugin_42bab598' // Source: ./vuetify/plugin.js (mode: 'all')
import nuxt_plugin_plugin_47557304 from 'nuxt_plugin_plugin_47557304' // Source: ./composition-api/plugin.mjs (mode: 'all')
import nuxt_plugin_veevalidate_e6e52ac2 from 'nuxt_plugin_veevalidate_e6e52ac2' // Source: ./vee-validate.js (mode: 'all')
import nuxt_plugin_markdownit_55a6828e from 'nuxt_plugin_markdownit_55a6828e' // Source: ./markdown-it.js (mode: 'all')
import nuxt_plugin_axios_2e0a522c from 'nuxt_plugin_axios_2e0a522c' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_gtm_2a91b402 from 'nuxt_plugin_gtm_2a91b402' // Source: ./gtm.js (mode: 'all')
import nuxt_plugin_nuxtdevicedetectplugin6896f47d_02921cf5 from 'nuxt_plugin_nuxtdevicedetectplugin6896f47d_02921cf5' // Source: ./nuxt-device-detect.plugin.6896f47d.js (mode: 'all')
import nuxt_plugin_moduleplugin499f4f68_217d232d from 'nuxt_plugin_moduleplugin499f4f68_217d232d' // Source: ./module.plugin.499f4f68.js (mode: 'all')
import nuxt_plugin_init_45f8bd9a from 'nuxt_plugin_init_45f8bd9a' // Source: ../plugins/init.client (mode: 'client')
import nuxt_plugin_eventBus_03aa9afc from 'nuxt_plugin_eventBus_03aa9afc' // Source: ../plugins/eventBus (mode: 'all')
import nuxt_plugin_blotout_34e03dee from 'nuxt_plugin_blotout_34e03dee' // Source: ../plugins/blotout (mode: 'client')
import nuxt_plugin_salesApi_668ad2e4 from 'nuxt_plugin_salesApi_668ad2e4' // Source: ../plugins/salesApi (mode: 'client')
import nuxt_plugin_luxon_3432ef10 from 'nuxt_plugin_luxon_3432ef10' // Source: ../plugins/luxon (mode: 'all')
import nuxt_plugin_vuexpersist_13f465a2 from 'nuxt_plugin_vuexpersist_13f465a2' // Source: ../plugins/vuex-persist (mode: 'client')
import nuxt_plugin_filters_98405076 from 'nuxt_plugin_filters_98405076' // Source: ../plugins/filters (mode: 'all')
import nuxt_plugin_vmask_33210aa8 from 'nuxt_plugin_vmask_33210aa8' // Source: ../plugins/vmask (mode: 'client')
import nuxt_plugin_noibu_34007936 from 'nuxt_plugin_noibu_34007936' // Source: ../plugins/noibu (mode: 'client')
import nuxt_plugin_meta_598eade0 from 'nuxt_plugin_meta_598eade0' // Source: ./composition-api/meta.mjs (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root.$options.$nuxt
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"htmlAttrs":{"lang":"en"},"meta":[{"charset":"utf-8"},{"hid":"robots","name":"robots","content":"none"},{"hid":"viewport","name":"viewport","content":"width=device-width, initial-scale=1.0"},{"hid":"msapplication-TileColor","name":"msapplication-TileColor","content":"#2b5797"},{"hid":"theme-color","name":"theme-color","content":"#1e3665"},{"hid":"apple-mobile-web-app-title","name":"apple-mobile-web-app-title","content":"Products 4Patriots"},{"hid":"og:site_name","property":"og:site_name","content":"Products 4Patriots"},{"hid":"og:type","property":"og:type","content":"website"}],"link":[{"rel":"apple-touch-icon","sizes":"180x180","href":"https:\u002F\u002Fcdn.4patriots.com\u002Fimg\u002Fgraphics\u002Flogos\u002Fapple-touch-icon.png"},{"rel":"icon","type":"image\u002Fpng","sizes":"32x32","href":"https:\u002F\u002Fcdn.4patriots.com\u002Fimg\u002Fgraphics\u002Flogos\u002Ffavicon-32x32.png"},{"rel":"icon","type":"image\u002Fpng","sizes":"16x16","href":"https:\u002F\u002Fcdn.4patriots.com\u002Fimg\u002Fgraphics\u002Flogos\u002Ffavicon-16x16.png"},{"rel":"mask-icon","href":"https:\u002F\u002Fcdn.4patriots.com\u002Fimg\u002Fgraphics\u002Flogos\u002Fsafari-pinned-tab.svg","color":"#1e3665"},{"rel":"preconnect","href":"https:\u002F\u002Ffonts.googleapis.com"},{"rel":"stylesheet","href":"https:\u002F\u002Ffonts.googleapis.com\u002Fcss?family=IBM+Plex+Sans:300,400,700|IBM+Plex+Sans+Condensed:700|Material+Icons&display=swap"},{"rel":"stylesheet","type":"text\u002Fcss","href":"https:\u002F\u002Fcdn.jsdelivr.net\u002Fnpm\u002F@mdi\u002Ffont@latest\u002Fcss\u002Fmaterialdesignicons.min.css"}],"style":[],"script":[{"hid":"gtm-script","innerHTML":"if(!window._gtm_init){window._gtm_init=1;(function(w,n,d,m,e,p){w[d]=(w[d]==1||n[d]=='yes'||n[d]==1||n[m]==1||(w[e]&&w[e][p]&&w[e][p]()))?1:0})(window,navigator,'doNotTrack','msDoNotTrack','external','msTrackingProtectionEnabled');(function(w,d,s,l,x,y){w[x]={};w._gtm_inject=function(i){w[x][i]=1;w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s);j.async=true;j.src='https:\u002F\u002Fwww.googletagmanager.com\u002Fgtm.js?id='+i;f.parentNode.insertBefore(j,f);};w[y]('GTM-MMJB7XT')})(window,document,'script','dataLayer','_gtm_ids','_gtm_inject')}"}],"noscript":[{"hid":"gtm-noscript","pbody":true,"innerHTML":"\u003Ciframe src=\"https:\u002F\u002Fwww.googletagmanager.com\u002Fns.html?id=GTM-MMJB7XT&\" height=\"0\" width=\"0\" style=\"display:none;visibility:hidden\" title=\"gtm\"\u003E\u003C\u002Fiframe\u003E"}],"__dangerouslyDisableSanitizersByTagID":{"gtm-script":["innerHTML"],"gtm-noscript":["innerHTML"]}},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (typeof nuxt_plugin_plugin_08283616 === 'function') {
    await nuxt_plugin_plugin_08283616(app.context, inject)
  }

  if (typeof nuxt_plugin_plugin_42bab598 === 'function') {
    await nuxt_plugin_plugin_42bab598(app.context, inject)
  }

  if (typeof nuxt_plugin_plugin_47557304 === 'function') {
    await nuxt_plugin_plugin_47557304(app.context, inject)
  }

  if (typeof nuxt_plugin_veevalidate_e6e52ac2 === 'function') {
    await nuxt_plugin_veevalidate_e6e52ac2(app.context, inject)
  }

  if (typeof nuxt_plugin_markdownit_55a6828e === 'function') {
    await nuxt_plugin_markdownit_55a6828e(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_2e0a522c === 'function') {
    await nuxt_plugin_axios_2e0a522c(app.context, inject)
  }

  if (typeof nuxt_plugin_gtm_2a91b402 === 'function') {
    await nuxt_plugin_gtm_2a91b402(app.context, inject)
  }

  if (typeof nuxt_plugin_nuxtdevicedetectplugin6896f47d_02921cf5 === 'function') {
    await nuxt_plugin_nuxtdevicedetectplugin6896f47d_02921cf5(app.context, inject)
  }

  if (typeof nuxt_plugin_moduleplugin499f4f68_217d232d === 'function') {
    await nuxt_plugin_moduleplugin499f4f68_217d232d(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_init_45f8bd9a === 'function') {
    await nuxt_plugin_init_45f8bd9a(app.context, inject)
  }

  if (typeof nuxt_plugin_eventBus_03aa9afc === 'function') {
    await nuxt_plugin_eventBus_03aa9afc(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_blotout_34e03dee === 'function') {
    await nuxt_plugin_blotout_34e03dee(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_salesApi_668ad2e4 === 'function') {
    await nuxt_plugin_salesApi_668ad2e4(app.context, inject)
  }

  if (typeof nuxt_plugin_luxon_3432ef10 === 'function') {
    await nuxt_plugin_luxon_3432ef10(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuexpersist_13f465a2 === 'function') {
    await nuxt_plugin_vuexpersist_13f465a2(app.context, inject)
  }

  if (typeof nuxt_plugin_filters_98405076 === 'function') {
    await nuxt_plugin_filters_98405076(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vmask_33210aa8 === 'function') {
    await nuxt_plugin_vmask_33210aa8(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_noibu_34007936 === 'function') {
    await nuxt_plugin_noibu_34007936(app.context, inject)
  }

  if (typeof nuxt_plugin_meta_598eade0 === 'function') {
    await nuxt_plugin_meta_598eade0(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
